// The top bar's search (spec §3.1). One box for three kinds of answer, shown together as the user types:
// - positions: OS and Irish grid references and coordinates, worked out in the browser;
// - the user's own routes, tracks and folders, by name, from the Library;
// - places by name, from the API's place search (OpenStreetMap), favouring those near the map view.
// Choosing a position or place flies the Plan map there and marks it with a pin; a route or track opens on the
// map; a folder opens in the Library. Beside it, Locate me does the same with the browser's position.
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { apiFetch } from '@/api/client';
import { getMapState } from '@/map/mapState';
import { toOsGridRef } from '@/map/projections';
import { useLibrary } from '@/features/library/api';
import type { ItemKind } from '@/features/library/model';
import { kindLabelKey } from '@/features/library/model';
import { openTrails } from '@/features/plan/openTrails';
import { parsePosition } from './parseQuery';
import type { SearchPin } from './searchState';
import { showPlace, zoomFor } from './searchState';
import styles from './SearchBox.module.css';

interface Place {
  name: string;
  kind: string;
  context: string | null;
  lon: number;
  lat: number;
  extent: [number, number, number, number] | null;
  osmType: string | null;
  osmId: number | null;
}

type Pin = Omit<SearchPin, 'n'>;

type Result =
  | { type: 'place'; key: string; label: string; detail?: string; pin: Pin }
  | { type: 'trail'; key: string; label: string; detail: string; kind: ItemKind; guid: string }
  | { type: 'folder'; key: string; label: string; detail: string; guid: string };

const RECENT_KEY = 'xped.search.recent';
const MAX_RECENT = 5;
const MAX_OWN = 5;

function readRecent(): Pin[] {
  try {
    const value = JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') as unknown;
    return Array.isArray(value) ? (value as Pin[]).slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function remember(pin: Pin) {
  try {
    const recent = [pin, ...readRecent().filter((p) => p.label !== pin.label || p.detail !== pin.detail)];
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {
    // Not remembered; the search still works.
  }
}

/** The query after the user stops typing for a moment. */
function useSettled(value: string, ms = 250) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

async function findPlaces(query: string, near: [number, number] | undefined, lang: string): Promise<Place[]> {
  const params = new URLSearchParams({ q: query, lang });
  if (near) {
    params.set('lon', near[0].toFixed(2));
    params.set('lat', near[1].toFixed(2));
  }
  const response = await apiFetch(`/search/places?${params}`);
  if (!response.ok) throw new Error(`Place search: ${response.status}`);
  return ((await response.json()) as { places: Place[] }).places;
}

export function SearchBox() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const container = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [chosenIndex, setActive] = useState(0);
  const [locating, setLocating] = useState<'busy' | 'failed' | null>(null);
  const query = text.trim();
  const settled = useSettled(query);
  const library = useLibrary();
  const lang = i18n.language.slice(0, 2);

  const positions = useMemo(() => parsePosition(query), [query]);
  // Rounded, so small moves of the map don't search again.
  const view = getMapState().view?.center;
  const near = view
    ? ([Number(view[0].toFixed(2)), Number(view[1].toFixed(2))] as [number, number])
    : undefined;
  const places = useQuery({
    queryKey: ['places', settled, near?.[0], near?.[1], lang],
    queryFn: () => findPlaces(settled, near, lang),
    enabled: open && settled.length >= 3 && positions.length === 0,
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  const results = useMemo<{ heading: string; items: Result[] }[]>(() => {
    if (!query) {
      const recent = readRecent();
      return recent.length
        ? [
            {
              heading: t('search.recent'),
              items: recent.map((pin, i) => ({
                type: 'place',
                key: `recent-${i}`,
                label: pin.label,
                detail: pin.detail,
                pin,
              })),
            },
          ]
        : [];
    }
    const groups: { heading: string; items: Result[] }[] = [];
    if (positions.length)
      groups.push({
        heading: t('search.positions'),
        items: positions.map((p) => {
          const detail =
            p.kind === 'osgb'
              ? t('search.osgb')
              : p.kind === 'irish'
                ? t('search.irish')
                : (toOsGridRef(...p.at) ?? t('search.coordinates'));
          return {
            type: 'place',
            key: `pos-${p.kind}`,
            label: p.label,
            detail,
            pin: { label: p.label, detail, at: p.at, zoom: 15 },
          };
        }),
      });

    // The user's own: names containing the query, those starting with it first.
    const needle = query.toLocaleLowerCase();
    const rank = (name: string) => {
      const at = name.toLocaleLowerCase().indexOf(needle);
      return at < 0 ? -1 : at === 0 ? 0 : 1;
    };
    const own: (Result & { rank: number })[] = [];
    for (const folder of library.data?.folders ?? []) {
      const r = rank(folder.name);
      if (r >= 0)
        own.push({
          type: 'folder',
          key: `folder-${folder.guid}`,
          label: folder.name,
          detail: t('library.folder'),
          guid: folder.guid,
          rank: r,
        });
    }
    for (const item of library.data?.items ?? []) {
      const r = rank(item.name);
      if (r >= 0)
        own.push({
          type: 'trail',
          key: `${item.kind}-${item.guid}`,
          label: item.name,
          detail: t(kindLabelKey(item.kind)),
          kind: item.kind,
          guid: item.guid,
          rank: r,
        });
    }
    own.sort((a, b) => a.rank - b.rank || a.label.localeCompare(b.label));
    if (own.length) groups.push({ heading: t('search.yours'), items: own.slice(0, MAX_OWN) });

    if (places.data?.length && settled === query)
      groups.push({
        heading: t('search.places'),
        items: places.data.map((p, i) => {
          const kind = p.kind.replace(/_/g, ' ');
          const detail = p.context ? `${kind} · ${p.context}` : kind;
          return {
            type: 'place',
            key: `place-${i}`,
            label: p.name,
            detail,
            pin: {
              label: p.name,
              detail,
              kind: p.kind,
              osm: p.osmType && p.osmId ? { type: p.osmType, id: p.osmId } : undefined,
              at: [p.lon, p.lat],
              extent: p.extent ?? undefined,
              zoom: zoomFor(p.kind),
            },
          };
        }),
      });
    return groups;
  }, [query, settled, positions, library.data, places.data, t]);

  const flat = results.flatMap((g) => g.items);
  // The highlighted result; results arriving can shorten the list under it.
  const active = Math.min(chosenIndex, Math.max(0, flat.length - 1));
  const searching = places.isFetching && settled.length >= 3;
  const status = !query
    ? null
    : searching
      ? t('search.searching')
      : places.isError
        ? t('search.failed')
        : flat.length === 0 && settled === query && (settled.length >= 3 || positions.length > 0)
          ? t('search.nothing')
          : null;

  // Close on a click elsewhere.
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => document.removeEventListener('pointerdown', onPointer);
  }, [open]);

  const choose = (result: Result) => {
    setOpen(false);
    input.current?.blur();
    if (result.type === 'place') {
      remember(result.pin);
      showPlace(result.pin);
      setText(result.label);
      void navigate('/plan');
    } else if (result.type === 'trail') {
      openTrails([{ kind: result.kind, guid: result.guid }]);
      setText('');
      void navigate('/plan');
    } else {
      setText('');
      void navigate(`/library?folder=${encodeURIComponent(result.guid)}`);
    }
  };

  const locate = () => {
    if (!navigator.geolocation) {
      setLocating('failed');
      return;
    }
    setLocating('busy');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(null);
        const at: [number, number] = [position.coords.longitude, position.coords.latitude];
        showPlace({ label: t('search.yourLocation'), detail: toOsGridRef(...at), at, zoom: 15 });
        void navigate('/plan');
      },
      () => setLocating('failed'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  useEffect(() => {
    if (locating !== 'failed') return;
    const timer = setTimeout(() => setLocating(null), 5000);
    return () => clearTimeout(timer);
  }, [locating]);

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOpen(true);
      if (flat.length)
        setActive((i) => (i + (event.key === 'ArrowDown' ? 1 : flat.length - 1)) % flat.length);
    } else if (event.key === 'Enter') {
      const result = flat[active];
      if (open && result) {
        event.preventDefault();
        choose(result);
      }
    } else if (event.key === 'Escape') {
      if (open) setOpen(false);
      else setText('');
    }
  };

  const showList = open && (flat.length > 0 || status !== null);
  let index = -1;

  return (
    <div className={styles.search} ref={container}>
      <input
        ref={input}
        className={styles.input}
        type="search"
        placeholder={t('search.placeholder')}
        aria-label={t('search.placeholder')}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && flat[active] ? `${listId}-${flat[active].key}` : undefined}
        value={text}
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className={styles.locate}
        aria-label={t('search.locate')}
        title={t('search.locate')}
        disabled={locating === 'busy'}
        onClick={locate}
      >
        <LocateIcon />
      </button>
      {locating === 'failed' && (
        <p className={styles.notice} role="alert">
          {t('search.locateFailed')}
        </p>
      )}
      {showList && (
        <div className={styles.list} id={listId} role="listbox" aria-label={t('search.results')}>
          {results.map((group) => (
            <div key={group.heading} role="group" aria-label={group.heading}>
              <div className={styles.heading} aria-hidden="true">
                {group.heading}
              </div>
              {group.items.map((result) => {
                index += 1;
                const i = index;
                return (
                  <div
                    key={result.key}
                    id={`${listId}-${result.key}`}
                    role="option"
                    aria-selected={i === active}
                    className={`${styles.option} ${i === active ? styles.active : ''}`}
                    onPointerMove={() => setActive(i)}
                    onPointerDown={(event) => event.preventDefault()}
                    onClick={() => choose(result)}
                  >
                    <span className={styles.label}>{result.label}</span>
                    {result.detail && <span className={styles.detail}>{result.detail}</span>}
                  </div>
                );
              })}
            </div>
          ))}
          {status && (
            <p className={styles.status} role="status">
              {status}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function LocateIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" strokeLinecap="round" />
    </svg>
  );
}
